import React, { useState } from "react";
import { Button, Input, Separator } from "@postcheesy/ui-lib/atoms";
import { Form, FormControl, FormItem, FormLabel, FormMessage, useToast } from "@postcheesy/ui-lib/molecules";
import { EyeIcon, EyeOffIcon } from "lucide-react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { changePasswordSchema } from "../../../resources/schema/changePassword-schema";
import axios from "axios";
import { useUser } from "../../../resources/contexts/UserContext";

interface ChangePasswordFormProps {
    setSuccess: React.Dispatch<React.SetStateAction<boolean>>;
}
export const ChangePasswordForm: React.FC<ChangePasswordFormProps> = ({ setSuccess }) => {
    const { toast } = useToast();
    const [isRequestPending, setIsRequestPending] = useState(false);
    const [showOldPassword, setShowOldPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const { user } = useUser();

    const changePasswordForm = useForm({
        resolver: zodResolver(changePasswordSchema),
        mode: "onBlur",
    });

    const handleChangePassword = async (data: z.infer<typeof changePasswordSchema>) => {
        if (isRequestPending) return;

        setIsRequestPending(true);
        try {
            const apiUrl = import.meta.env.VITE_API_URL;

            if (!user) {
                toast({
                    title: "Erreur",
                    description: "Utilisateur non trouvé. Veuillez vous reconnecter.",
                    variant: "destructive",
                });
                return;
            }

            const updateData = {
                oldPassword: data.oldPassword,
                newPassword: data.newPassword,
                nom: user.nom ?? "",
                prenom: user.prenom ?? "",
                codePostal: user.codePostal,
                magasinId: user.magasinId,
                isEmailArrivageCoupDeCoeur: user.isEmailArrivageCoupDeCoeur ?? false,
                isEmailEvents: user.isEmailEvents ?? false,
                isEmailWhatIsNewExclusif: user.isEmailWhatIsNewExclusif ?? false,
            };

            const response = await axios.post(
                `${apiUrl}/manage/info`,
                updateData,
                { withCredentials: true }
            );

            if (response.status === 200) {
                setSuccess(true);
                toast({ description: "Votre mot de passe a été mis à jour avec succès.", variant: "default" });
                changePasswordForm.reset();
            } else {
                toast({
                    title: "Erreur",
                    description: "Impossible de changer le mot de passe. Veuillez réessayer.",
                    variant: "destructive",
                });
            }
        } catch (error) {
            // console.error("Erreur lors du changement de mot de passe:", error);
            toast({
                title: "Erreur",
                description: "Impossible de changer le mot de passe. Veuillez réessayer.",
                variant: "destructive",
            });
        } finally {
            setIsRequestPending(false);
        }
    };

    return (
        <Form {...changePasswordForm}>
            <form onSubmit={changePasswordForm.handleSubmit((data) => handleChangePassword(data as z.infer<typeof changePasswordSchema>))} className="space-y-4">
                <FormItem>
                    <FormLabel>Ancien mot de passe</FormLabel>
                    <FormControl>
                        <div className="flex items-center">
                            <Controller
                                control={changePasswordForm.control}
                                name="oldPassword"
                                render={({ field }) => (
                                    <Input
                                        type={showOldPassword ? "text" : "password"}
                                        placeholder="Entrez votre ancien mot de passe"
                                        {...field}
                                    />
                                )}
                            />
                            <Button type="button" className="h-9" onClick={() => setShowOldPassword(!showOldPassword)}>
                                {showOldPassword ? <EyeIcon className="w-4 h-4" /> : <EyeOffIcon className="w-4 h-4" />}
                            </Button>
                        </div>
                    </FormControl>
                    <FormMessage>{changePasswordForm.formState.errors.oldPassword?.message?.toString()}</FormMessage>
                </FormItem>
                <Separator />
                <FormItem>
                    <FormLabel>Nouveau mot de passe</FormLabel>
                    <FormControl>
                        <div className="flex items-center">
                            <Controller
                                control={changePasswordForm.control}
                                name="newPassword"
                                render={({ field }) => (
                                    <Input
                                        type={showNewPassword ? "text" : "password"}
                                        placeholder="Entrez votre nouveau mot de passe"
                                        {...field}
                                    />
                                )}
                            />
                            <Button type="button" className="h-9" onClick={() => setShowNewPassword(!showNewPassword)}>
                                {showNewPassword ? <EyeIcon className="w-4 h-4" /> : <EyeOffIcon className="w-4 h-4" />}
                            </Button>
                        </div>
                    </FormControl>
                    <FormMessage>{changePasswordForm.formState.errors.newPassword?.message?.toString()}</FormMessage>
                </FormItem>

                <FormItem>
                    <FormLabel>Confirmer le mot de passe</FormLabel>
                    <FormControl>
                        <div className="flex items-center">
                            <Controller
                                control={changePasswordForm.control}
                                name="confirmPassword"
                                render={({ field }) => (
                                    <Input
                                        type={showConfirmPassword ? "text" : "password"}
                                        placeholder="Confirmez votre mot de passe"
                                        {...field}
                                    />
                                )}
                            />
                            <Button type="button" className="h-9" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                                {showConfirmPassword ? <EyeIcon className="w-4 h-4" /> : <EyeOffIcon className="w-4 h-4" />}
                            </Button>
                        </div>
                    </FormControl>
                    <FormMessage>{changePasswordForm.formState.errors.confirmPassword?.message?.toString()}</FormMessage>
                </FormItem>

                <Button type="submit" className="w-full" disabled={isRequestPending || !changePasswordForm.formState.isValid}>
                    {isRequestPending ? "Mise à jour..." : "Changer mon mot de passe"}
                </Button>
            </form>
        </Form>
    );
};